import { useGesture } from '@use-gesture/react'
import { RefObject, useCallback, useContext, useEffect, useState } from 'react'

import { ControllerContext } from '@Components/Product/Slider/Context/ControllerContext'
import { SliderContext } from '@Components/Product/Slider/Context/SliderContext'
import { applyZoomTransition } from '@Components/Slider/utils/helpers'

interface CoordinateMap {
  scale: number
  x: number
  y: number
}

const initialTransform: CoordinateMap = { scale: 1, x: 0, y: 0 }

const MAXIMUM_SCALE = 3
const THRESHOLD = 5
const MINIMUM_SCALE = 1.01
const FACTOR = 2

const useImageControl = <T extends HTMLElement>(imageRef: RefObject<T>, imageContainerRef: RefObject<T>) => {
  const {
    disableDrag,
    isZoomedIn,
    setZoomedIn,
  } = useContext(ControllerContext)
  const {
    activeSlide,
    isFullscreen,
    isSwipedUp,
    setActiveSlide,
    setSwipeUp,
    toggleFullscreen,
  } = useContext(SliderContext)
  const [isPinching, setIsPinching] = useState(false)
  const [isAnimating, setAnimate] = useState(false)
  const [controlState, setControlState] = useState<CoordinateMap>(initialTransform)

  const handleTransform = (props: Partial<CoordinateMap>) => {
    setControlState(prevState => ({
      ...prevState,
      ...props,
    }))
  }

  const canMoveDirection = (imageBoundsSize, containerBoundsSize, originalSize, value) => {
    const overhang = (imageBoundsSize - originalSize) / 2
    const maxX = overhang
    const minX = -(imageBoundsSize - containerBoundsSize) + overhang
    if (value > maxX) {
      return maxX
    }
    if (value < minX) {
      return minX
    }
    return value
  }

  const mayMoveX = (x) => {
    if (imageRef.current && imageContainerRef.current) {
      const imageBounds = imageRef.current.getBoundingClientRect()
      const containerBounds = imageContainerRef.current.getBoundingClientRect()
      const originalWidth = imageRef.current.clientWidth
      return canMoveDirection(imageBounds.width, containerBounds.width, originalWidth, x)
    }
    return x
  }
  const mayMoveY = (y) => {
    if (imageRef.current && imageContainerRef.current) {
      const imageBounds = imageRef.current.getBoundingClientRect()
      const containerBounds = imageContainerRef.current.getBoundingClientRect()
      const originalHeight = imageRef.current.clientHeight
      return canMoveDirection(imageBounds.height, containerBounds.height, originalHeight, y)
    }
    return y
  }

  const changeZoom = useCallback((instantTransition: boolean) => {
    if (imageRef.current && !isPinching) {
      applyZoomTransition(imageRef, instantTransition ? '0s' : '0.3s')
      setIsPinching(false)
      setZoomedIn(false)
      disableDrag(false)
      handleTransform(initialTransform)
    }
  }, [disableDrag, imageRef, isPinching, setZoomedIn])

  const resetZoom = useCallback(() => {
    if (isZoomedIn && imageRef.current) {
      changeZoom(false)
    }
  }, [changeZoom, imageRef, isZoomedIn])

  const updateActiveSlider = useCallback(() => {
    if (!isFullscreen) {
      setActiveSlide({ key: activeSlide.key, recalculate: false })
      changeZoom(true)
    }
  }, [isFullscreen, setActiveSlide, activeSlide.key, changeZoom])
  const updateZoom = useCallback(() => {
    if (!isZoomedIn) {
      changeZoom(false)
    }
  }, [isZoomedIn, changeZoom])

  useEffect(() => {
    if (!isFullscreen) {
      updateActiveSlider()
    } else if (!isZoomedIn) {
      updateZoom()
    }
  }, [isFullscreen, isZoomedIn, updateActiveSlider, updateZoom])

  const handleZoom = useCallback((event?) => {
    applyZoomTransition(imageRef)
    if (imageRef.current && !isPinching) {
      // eslint-disable-next-line no-param-reassign
      imageRef.current.style.cursor = isZoomedIn ? 'pointer' : 'default'
      if (event && !isZoomedIn) {
        const container = imageContainerRef.current?.getBoundingClientRect()
        if (container) {
          const x = container.right / 2 - (event.clientX ?? event.changedTouches[0].clientX)
          const y = container.bottom / 2 - (event.clientY ?? event.changedTouches[0].clientY)
          handleTransform({ x, y, scale: MAXIMUM_SCALE })
          disableDrag(true)
        }
      } else {
        resetZoom()
      }
    }
    setZoomedIn(!isZoomedIn)
  }, [disableDrag, imageContainerRef, imageRef, isPinching, isZoomedIn, resetZoom, setZoomedIn])

  useGesture({
    onDrag: ({
      event,
      memo,
      offset,
      pinching,
      swipe: [, verticalSwipe],
      tap,
    }) => {
      if (isPinching) {
        return null
      }
      if (isFullscreen && verticalSwipe === -1 && !isZoomedIn) {
        setSwipeUp(true)
        return null
      }
      if (tap) {
        if (isFullscreen) {
          handleZoom(event)
        } else {
          toggleFullscreen()
        }
        return memo
      }

      if (pinching || !isZoomedIn) {
        return memo
      }
      setAnimate(true)

      const [x, y] = offset
      // eslint-disable-next-line no-param-reassign
      memo ??= {
        xPos: controlState.x,
        yPos: controlState.y,
        x,
        y,
      }

      let xPos = (memo.xPos + (x - memo.x) / controlState.scale)
      let yPos = (memo.yPos + (y - memo.y) / controlState.scale)

      const boundingClientRect = imageRef.current!.getBoundingClientRect()
      if ((boundingClientRect.top > 0 && yPos > memo.yPos)
        || (boundingClientRect.bottom < window.innerHeight && yPos < memo.yPos)) {
        yPos = memo.yPos
      }
      if ((boundingClientRect.left > 0 && xPos > memo.xPos)
        || (boundingClientRect.right < window.innerWidth && xPos < memo.xPos)) {
        xPos = memo.xPos
      }

      handleTransform({ x: xPos, y: yPos })

      // eslint-disable-next-line no-param-reassign
      memo = { xPos, yPos, x, y }
      return memo
    },
    onPinch: ({
      memo,
      origin: [pinchOriginX, pinchOriginY],
      movement: [d],
    }) => {
      if (imageRef.current && !isAnimating && isFullscreen) {
        // eslint-disable-next-line no-param-reassign
        memo ??= {
          bounds: imageRef.current.getBoundingClientRect(),
          pinchState: controlState,
          originScale: controlState.scale,
          prevD: d,
        }

        if (d === 1) {
          return memo
        }
        const zoomingIN = memo.prevD < d
        let scale = memo.originScale * d
        if (zoomingIN && scale < memo.originScale) {
          scale = memo.originScale
        }
        if (!zoomingIN && scale > memo.originScale) {
          scale = memo.originScale
        }
        const isOutScaleRange = scale <= MINIMUM_SCALE || scale >= MAXIMUM_SCALE
        if (isOutScaleRange) {
          setControlState(pinch => ({
            ...pinch,
            scale: scale <= MINIMUM_SCALE ? MINIMUM_SCALE : MAXIMUM_SCALE,
          }))
          // eslint-disable-next-line no-param-reassign
          return memo
        }

        const transformOriginX = memo.bounds.x + memo.bounds.width / FACTOR
        const transformOriginY = memo.bounds.y + memo.bounds.height / FACTOR

        const displacementFactor = scale / MAXIMUM_SCALE
        const displacementX = ((transformOriginX - pinchOriginX) / scale) * displacementFactor
        const displacementY = ((transformOriginY - pinchOriginY) / scale) * displacementFactor

        const initialOffsetDistance = (memo.pinchState.scale - 1) * FACTOR
        const movementDistance = d - initialOffsetDistance
        const newX = zoomingIN ? memo.pinchState.x + (displacementX * movementDistance) / FACTOR : controlState.x
        const newY = zoomingIN ? memo.pinchState.y + (displacementY * movementDistance) / FACTOR : controlState.y

        handleTransform({
          scale,
          x: mayMoveX(newX),
          y: mayMoveY(newY),
        })
      }
      return memo
    },
    onPinchStart: () => {
      applyZoomTransition(imageRef, '0s')
      setZoomedIn(true)
      disableDrag(true)
      setIsPinching(true)
    },
    onPinchEnd: () => {
      disableDrag(false)
      setIsPinching(false)
      if (controlState.scale > 1) {
        setZoomedIn(true)
      }
      if (controlState.scale <= MINIMUM_SCALE) {
        applyZoomTransition(imageRef, '0s')
        setZoomedIn(false)
        return
      }
      applyZoomTransition(imageRef)
    },
    onDragStart: () => {
      applyZoomTransition(imageRef, '0s')
    },
    onDragEnd: () => {
      applyZoomTransition(imageRef)
    },
  }, {
    target: imageRef,
    eventOptions: { passive: false },
    drag: { threshold: THRESHOLD, filterTaps: true, pointer: { touch: true } },
  })

  return {
    reset: resetZoom,
    isPinching,
    isZoomedIn,
    controlState,
    setAnimate,
    isSwipedUp,
    setSwipeUp,
  }
}

export default useImageControl
